
/* eslint-disable @typescript-eslint/no-explicit-any */
import { computed, defineComponent, PropType, ref, watchEffect } from 'vue';
import BaseLoadingSpinner from '@/components/BaseLoadingSpinner.vue';

interface Column {
  name: string;
  align: 'center' | 'left' | 'right';
  type: ColumnType;
}

type ColumnType = 'number' | 'string' | 'date' | null;
type ColumnState = 'up' | 'down' | 'none';

export default defineComponent({
  name: 'BaseTable',
  components: {
    BaseLoadingSpinner,
  },
  props: {
    columns: {
      type: Object as PropType<{ [key: string]: Column }>,
      required: true,
    },
    rows: {
      type: Array as PropType<{ [key: string]: string }[]>,
      default: null,
    },
  },
  emits: ['sortBy'],
  setup(props) {
    const alertActive = ref('polite');
    const ariaBusy = ref(true);
    const columnState = ref<ColumnState>('none');
    const columnSorted = ref<{
      name: string;
      type: ColumnType;
    }>({
      name: '',
      type: 'string',
    });

    const sortedRows = computed(() => {
      if (props.rows) {
        return _sortRows(props.rows, columnSorted.value, columnState.value);
      }
      return null;
    });

    watchEffect(() => {
      if (props.rows != null) {
        alertActive.value = 'off';
        ariaBusy.value = false;
      }
    });

    function sortBy(column: Column) {
      _setColumnState(column.name);
      columnSorted.value = { name: column.name, type: column.type };
    }

    function _setColumnState(newColumnName: string) {
      if (
        columnSorted.value.name !== '' &&
        columnSorted.value.name !== newColumnName
      ) {
        columnState.value = 'up';
        return;
      }
      if (columnState.value == 'none') {
        columnState.value = 'up';
      } else if (columnState.value == 'up') {
        columnState.value = 'down';
      } else {
        columnState.value = 'none';
        columnSorted.value = { name: '', type: 'string' };
      }
    }

    function _sortRows(
      rows: { [key: string]: any }[],
      column: {
        name: string;
        type: ColumnType;
      },
      columnState: ColumnState
    ) {
      if (column.type == null || column.type == 'string') {
        return _sortRowsByCol(rows, column.name, columnState);
      }
    }

    function _sortRowsByCol(
      rows: { [key: string]: any }[],
      columnName: string,
      columnState: ColumnState
    ) {
      if (columnState == 'up') {
        return [...rows].sort((a, b) =>
          a[columnName] < b[columnName]
            ? -1
            : a[columnName] === b[columnName]
            ? 0
            : 1
        );
      } else if (columnState == 'down') {
        return [...rows].sort((a, b) =>
          a[columnName] > b[columnName]
            ? -1
            : a[columnName] === b[columnName]
            ? 0
            : 1
        );
      } else {
        return [...rows];
      }
    }

    return {
      alertActive,
      ariaBusy,
      sortBy,
      sortedRows,
      columnState,
      columnSorted,
    };
  },
});
